import { useEffect, useState } from 'react';

import { message } from 'antd';
import { arrayMoveImmutable } from 'array-move';
import { v4 as uuidv4 } from 'uuid';

import {
  UploadAppendFileTemps,
  UploadFileTemp,
  UploadFileTemps,
  UploadProps,
  UploadRemoveFileTemps,
} from '../types';

type Props = UploadProps;

export function useUploadTemps(props: Props) {
  const { fileList, onChange = () => {} } = props;
  const [fileTemps, setFileTemps] = useState<UploadFileTemps>([]);
  const [standbyFiles, setStandbyFiles] = useState<UploadFileTemps>([]);

  const handleGenerateFileTemps = () => {
    if (!Array.isArray(fileList)) {
      return;
    }
    const generatedFileTemps: UploadFileTemps = fileList.map((response) => ({
      id: uuidv4(),
      status: 'done',
      response,
    }));
    setFileTemps((prev) => {
      // done 상태가 아닌 것만 찾기
      const filteredFileTemps = prev.filter((e) => e.status !== 'done');

      return [...generatedFileTemps, ...filteredFileTemps];
    });
  };

  /**
   * fileList가 변경되면 fileTemps에 반영합니다.
   *
   */
  useEffect(() => {
    handleGenerateFileTemps();
  }, [fileList]);

  /**
   * fileTemps를 origin 기준으로 초기화합니다.
   */
  const snycFileTemps = () => {
    const generatedFileTemps: UploadFileTemps = fileList.map((response) => ({
      id: uuidv4(),
      status: 'done',
      response,
    }));

    setFileTemps(generatedFileTemps);
  };

  const moveFileTemps = (fromIndex: number, toIndex: number) => {
    const changedFileList = arrayMoveImmutable(fileList, fromIndex, toIndex);
    onChange(changedFileList);
  };

  const resetFileTemps = () => {
    onChange([]);
  };

  /**
   * 업로드할 파일을 fileTemps에 저장합니다.
   */
  const appendFileTemps: UploadAppendFileTemps = (files, inputEvent) => {
    const clearInputValue = () => {
      if (!inputEvent) return;
      inputEvent.target.value = '';
    };

    // 첨부된 파일이 없을 경우
    if (!files?.length) {
      message.error('파일이 정상적으로 선택되지 않았습니다. 파일을 다시 선택해 주세요');
      clearInputValue();
      return;
    }

    // 파일 데이터를 배열로 변환
    const newStandbyFiles: UploadFileTemps = [...Array(files.length)].map((_, index) => {
      return {
        id: uuidv4(),
        file: files[index],
        status: 'ready',
      };
    });

    // 업로드할 파일을 저장
    setStandbyFiles(newStandbyFiles);

    // 디스플레이할 업로드 데이터를 저장
    setFileTemps((prev) => [...prev, ...newStandbyFiles]);

    // input value를 초기화
    clearInputValue();
  };

  /**
   * temps의 특정 row의 정보를 변경합니다.
   * 보통 status를 변경하는 데에 사용합니다.
   * @param id 변경할 row의 id
   * @param changeValue
   */
  const modifyFileTemps = (id: string, changeValue: Partial<UploadFileTemp>) => {
    setFileTemps((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        return {
          ...item,
          ...changeValue,
        };
      }),
    );
  };

  /**
   * temp와 origin 모두에서 일치하는 데이터를 삭제하는 로직
   * @param item 삭제할 row의 record
   */
  const removeFileTemps: UploadRemoveFileTemps = (item) => {
    // response가 있을 경우(업로드 완료된 상태일 경우) origin에서 제거
    if (item.status === 'done' && item?.response) {
      onChange(fileList.filter((e) => e.url !== item.response?.url));
    }

    // temps에서 제거
    setFileTemps((prev) => prev.filter((e) => e.id !== item.id));
  };

  const resetStandbyFiles = () => setStandbyFiles([]);

  return {
    fileTemps,
    standbyFiles,
    setFileTemps,
    appendFileTemps,
    modifyFileTemps,
    removeFileTemps,
    moveFileTemps,
    snycFileTemps,
    resetFileTemps,
    resetStandbyFiles,
  };
}
